import React, { useRef } from "react";
import { motion } from "framer-motion";
import SectionHeading from "./SectionHeading";
import { capabilities, toolbox } from "../constants/data";

const toneStyles = {
    blue: "border-[#B9C4FF] bg-[#EEF1FF] text-[#2B44FF] dark:border-[#6B8AFF] dark:bg-[#1A2248] dark:text-[#B8C8FF]",
    green: "border-[#B5E0C2] bg-[#EAF7EE] text-[#1E7B3C] dark:border-[#C8F65D] dark:bg-[#1C2410] dark:text-[#D4F98A]",
    orange: "border-[#FFCBB5] bg-[#FFF0E8] text-[#D14A12] dark:border-[#FF9B75] dark:bg-[#2A1810] dark:text-[#FFC4A8]",
    violet: "border-[#D8C6F5] bg-[#F4EDFE] text-[#7C3AED] dark:border-[#C4B5FD] dark:bg-[#22183A] dark:text-[#E4D9FF]",
};

const scatter = (i) => {
    const cols = 6;
    const col = i % cols;
    const row = Math.floor(i / cols);
    const stagger = (row % 2) * 7;
    const jitterY = ((i * 53) % 7) - 3;
    return {
        left: `${4 + col * 15 + stagger}%`,
        top: `${12 + row * 28 + jitterY}%`,
        rotate: ((i * 41) % 13) - 6,
    };
};

const Toolbox = () => {
    const boxRef = useRef(null);

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14"
        >
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-xl font-medium text-fg">
                    Tech stack{" "}
                    <span className="font-serif italic text-muted">
                        — drag to explore
                    </span>
                </h3>
                <span className="font-mono text-[11px] uppercase tracking-widest text-faint">
                    interactive · not a résumé dump
                </span>
            </div>
            <p className="mb-5 max-w-2xl text-sm leading-relaxed text-muted">
                The tools I actually reach for when shipping — frontend, backend,
                AI and infrastructure. Drag the chips around while you skim; it's
                a map of the stack, not a decoration.
            </p>

            <div
                ref={boxRef}
                className="relative h-[340px] touch-none overflow-hidden rounded-2xl border border-line bg-elevated sm:h-[300px]"
                style={{
                    backgroundImage:
                        "radial-gradient(var(--dot-grid) 1px, transparent 1px)",
                    backgroundSize: "22px 22px",
                }}
            >
                {toolbox.map((tool, i) => {
                    const pos = scatter(i);
                    return (
                        <motion.button
                            key={tool.name}
                            type="button"
                            drag
                            dragConstraints={boxRef}
                            dragElastic={0.2}
                            dragMomentum={true}
                            whileDrag={{ scale: 1.12, zIndex: 20, rotate: 0 }}
                            whileHover={{ scale: 1.06 }}
                            initial={{ opacity: 0, scale: 0.6, rotate: pos.rotate }}
                            whileInView={{ opacity: 1, scale: 1, rotate: pos.rotate }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            viewport={{ once: true }}
                            style={{ left: pos.left, top: pos.top }}
                            className={`absolute cursor-grab select-none rounded-full border px-4 py-2 font-mono text-xs shadow-sm active:cursor-grabbing sm:text-[13px] ${toneStyles[tool.tone]}`}
                        >
                            {tool.name}
                        </motion.button>
                    );
                })}
            </div>
        </motion.div>
    );
};

const Capabilities = () => {
    return (
        <section id="what" className="py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeading
                    index="01"
                    label="What I Do"
                    title={
                        <>
                            Four ways I make myself{" "}
                            <span className="font-serif italic text-accent">
                                useful
                            </span>
                            .
                        </>
                    }
                />

                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                    {capabilities.map((cap, i) => (
                        <motion.div
                            key={cap.index}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            viewport={{ once: true, margin: "-60px" }}
                            whileHover={{ y: -6 }}
                            className={`rounded-2xl border p-6 transition-shadow hover:shadow-lift ${
                                cap.focus
                                    ? "border-accent/30 bg-[var(--focus-card)]"
                                    : "border-line bg-elevated"
                            }`}
                        >
                            <span
                                className={`font-mono text-xs ${
                                    cap.focus ? "text-accent" : "text-faint"
                                }`}
                            >
                                /{cap.index}
                            </span>
                            <h3 className="mb-3 mt-5 text-lg font-medium text-fg">
                                {cap.title}
                                {cap.focus && (
                                    <span className="ml-2 align-middle rounded-full bg-accent px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-base dark:text-[#0a0a0b]">
                                        focus
                                    </span>
                                )}
                            </h3>
                            <p className="text-sm leading-relaxed text-muted">
                                {cap.blurb}
                            </p>
                        </motion.div>
                    ))}
                </div>

                <Toolbox />
            </div>
        </section>
    );
};

export default Capabilities;